<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import type {
  GetCrmSegmentsByIdResponses,
  PostCrmSegmentsByIdStatsResponses,
} from '@/types/openapi-ts';
//#endregion

//#region types
type SegmentStats = Partial<PostCrmSegmentsByIdStatsResponses[200]['payload']>;
type SegmentModerationStatus = GetCrmSegmentsByIdResponses[200]['payload']['moderation_status'];
type SegmentViewType = 'edit' | 'view';
type ActivitySegmentType = 'global' | '';
type ShowFiltersMode = 'none' | 'edit';

interface Props {
  stats?: SegmentStats | null;
  statsLoader?: boolean;
  playerAttached?: boolean;
  segmentType?: SegmentViewType;
  activitySegmentType?: ActivitySegmentType;
  moderationStatus?: SegmentModerationStatus | '';
  showFiltersMode?: ShowFiltersMode;
}

interface Emits {
  exportData: [];
  refreshStats: [];
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  stats: null,
  statsLoader: false,
  playerAttached: false,
  segmentType: 'edit',
  activitySegmentType: '',
  moderationStatus: '',
  showFiltersMode: 'none',
});

const emit = defineEmits<Emits>();
//#endregion

//#region computed
const canRefreshStats = computed(() => {
  const isEditMode = props.segmentType === 'edit';
  const isGlobalEditMode = props.activitySegmentType === 'global' && props.showFiltersMode === 'edit';

  return isEditMode || isGlobalEditMode;
});

const canExportData = computed(() => props.segmentType === 'view' && props.moderationStatus === 'approved');

const hideStats = computed(() => {
  return !canRefreshStats.value && props.activitySegmentType !== 'global' && !props.playerAttached;
});
//#endregion

//#region methods
function refreshStats() {
  emit('refreshStats');
}

function exportData() {
  emit('exportData');
}
//#endregion
</script>

<template>
  <div
    v-loading="statsLoader || hideStats"
    class="segment-info"
  >
    <div class="t:mb-12">
      <CardTitle :title="$t('crm.segments.stats.players')">
        <div class="t:flex t:space-x-8">
          <UiAction
            v-if="canRefreshStats"
            icon="sync-alt"
            :action-name="$t('crm.segments.stats.refresh')"
            @click="refreshStats()"
          />
          <UiAction
            v-if="canExportData"
            icon="download"
            :action-name="$t('mediaBuying.info.files_card.download')"
            @click="exportData()"
          />
        </div>
      </CardTitle>
      <UiLabel
        class="t:p-8"
        :label="stats?.players_count || '0'"
        :font-size="24"
        bold
      />
    </div>
    <CardBlock
      :title="$t('crm.segments.stats.statistics')"
      :list="[
        {
          name: $t('crm.segments.stats.deposit'),
          value: `${stats?.deposits_all_avg || '$0.00'} / ${stats?.deposits_all_median || '$0.00'}`,
        },
        {
          name: $t('crm.segments.stats.sport_bet'),
          value: `${stats?.avg_sports_bet_amount || '$0.00'} / ${stats?.median_sports_bet_amount || '$0.00'}`,
        },
        {
          name: $t('crm.segments.stats.casino_bet'),
          value: `${stats?.avg_games_bet_amount || '$0.00'} / ${stats?.median_games_bet_amount || '$0.00'}`,
        }
      ]"
    >
      <template v-slot:action>
        <UiLabel
          :label="$t('crm.segments.stats.avg_med')"
          bold
        />
      </template>
    </CardBlock>
  </div>
</template>
